import Image from "next/image";

type SmartImageProps = {
  src?: string | null;
  alt?: string;
  className?: string;
  width?: number;
  height?: number;
  fill?: boolean;
  sizes?: string;
  priority?: boolean;
  /** Quality of the optimized image (1-100). Only applies to optimized images. Default: Next.js default (75). */
  quality?: number;
};

const ALLOWED_HOST_PATTERNS = [
  /\.amazonaws\.com$/i,
  /\.hsforms\.com$/i,
  /\.hscollectedforms\.net$/i,
  /\.hsforms\.net$/i,
  /\.youtube\.com$/i,
  /\.ytimg\.com$/i,
  /\.hubspot\.com$/i,
  /\.bizible\.com$/i,
  /\.linkedin\.com$/i,
  /\.bizibly\.com$/i,
  /\.cisco\.com$/i,
  /\.google-analytics\.com$/i,
  /\.googletagmanager\.com$/i,
  /^cdn\.cookielaw\.org$/i,
  /\.6sc\.co$/i,
  /\.inpwrd\.com$/i,
  /\.inpwrd\.net$/i,
];

function isSafeToOptimize(urlStr: string): boolean {
  if (urlStr.startsWith("/")) return true;

  try {
    const parsed = new URL(urlStr);
    return ALLOWED_HOST_PATTERNS.some((pattern) => pattern.test(parsed.hostname));
  } catch {
    return false;
  }
}

export default function SmartImage({
  src,
  alt = "",
  className,
  width,
  height,
  fill = false,
  sizes,
  priority = false,
  quality,
}: SmartImageProps) {
  if (!src) return null;

  const canOptimize = isSafeToOptimize(src);

  // Props safe for both Next.js <Image> and plain <img>
  const commonProps = {
    src,
    alt,
    className,
  };

  if (fill || (width && height)) {
    return (
      <Image
        {...commonProps}
        {...(fill ? { fill: true } : { width, height })}
        sizes={sizes ?? (fill ? "100vw" : undefined)}
        priority={priority}
        loading={priority ? ("eager" as const) : ("lazy" as const)}
        fetchPriority={priority ? ("high" as const) : ("auto" as const)}
        unoptimized={!canOptimize}
        quality={quality}
        data-modulename={canOptimize ? "SmartImage" : "SmartImage-Unoptimized"}
      />
    );
  }

  return (
    <img
      {...commonProps}
      sizes={sizes}
      loading="lazy"
      // quality is intentionally not applied here — it is only valid for the Next.js image optimizer
      data-modulename="RegImage-Legacy"
    />
  );
}
